import { auth } from '@/firebase';
import sl from '@/serviceLocator';
import {
  startAdminBadgePolling,
  stopAdminBadgePolling,
} from '@/utils/adminBadgePolling';

// A rotation within this long after a successful load skips the reload; the
// interceptor's forced refreshes and a cold load's second event land here.
const RELOAD_AFTER_MS = 60 * 1000;

// The load in progress, shared by every event for the same uid while pending.
let loadInFlight: { uid: string; promise: Promise<boolean> } | null = null;
// When the user row and permissions were last loaded successfully; null when
// signed out or after a failed load, so the next event loads again.
let lastLoad: { uid: string; at: number } | null = null;

// Resolves false when the load failed, so an event that joined it can retry.
const loadUser = async (uid: string): Promise<boolean> => {
  const store = sl.get('store');
  try {
    const server = sl.get('serverProxy');
    const [oareUser, permissions] = await Promise.all([
      server.getUser(uid),
      server.getUserPermissions(),
    ]);
    // A sign-out or a switch to another account may have landed while this
    // load was in flight; its result would overwrite the newer state.
    if (auth.currentUser?.uid !== uid) {
      return true;
    }
    store.setPermissions(permissions);
    store.setUser(oareUser);
    lastLoad = { uid, at: Date.now() };

    // After every load, so a promotion or demotion mid-session takes effect.
    // Starting is a no-op while a poll runs; it runs in the background, not
    // ahead of first render.
    if (store.getters.isAdmin) {
      startAdminBadgePolling();
    } else {
      stopAdminBadgePolling();
    }
    return true;
  } catch (err) {
    // Let the next event load again instead of skipping it.
    lastLoad = null;
    // Fire and forget: the log goes to the same backend that just failed,
    // and its rejection must not stop the app from mounting.
    sl.get('globalActions')
      .logError('Error initializing site', err as Error)
      .catch(() => {});
    return false;
  }
};

/**
 * Keep the store in step with Firebase auth. Runs for every onIdTokenChanged
 * event: sign-in, sign-out, and each token rotation (about hourly).
 * - Sign-in (or first load): load the user and permissions.
 * - Rotation for the same uid: reload them, so a changed permission reaches a
 *   long-lived tab. An event while a load is pending shares it, and one
 *   within a minute of the last load skips the reload.
 * - After each load: run the admin badge poll if the user is an admin, and
 *   stop it otherwise.
 * - Sign-out: clear the store and stop the poll.
 */
export const handleIdTokenChange = async (
  user: { uid: string } | null
): Promise<void> => {
  if (!user) {
    loadInFlight = null;
    lastLoad = null;
    sl.get('store').logout();
    stopAdminBadgePolling();
    return;
  }

  if (loadInFlight?.uid === user.uid) {
    if (await loadInFlight.promise) {
      return;
    }
    // The shared load failed. Retry only if this user is still signed in
    // (a sign-out may have overtaken the load), joining a retry another
    // event already started, or starting one below (one retry per event).
    if (auth.currentUser?.uid !== user.uid) {
      return;
    }
    if (loadInFlight?.uid === user.uid) {
      await loadInFlight.promise;
      return;
    }
  }

  if (
    lastLoad?.uid === user.uid &&
    Date.now() - lastLoad.at < RELOAD_AFTER_MS
  ) {
    return;
  }

  const promise = loadUser(user.uid);
  loadInFlight = { uid: user.uid, promise };
  try {
    await promise;
  } finally {
    if (loadInFlight?.promise === promise) {
      loadInFlight = null;
    }
  }
};
